import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes, 
  FaSpinner,
  FaBriefcase,
  FaBuilding
} from 'react-icons/fa';
import { experienceAPI } from '../../utils/api.js';
import { uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';

const ExperienceEditor = () => {
  const [experiences, setExperiences] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingExperience, setEditingExperience] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newExperience, setNewExperience] = useState({
    role: '',
    company: '',
    duration: '',
    summary: '',
    visible: true
  });

  useEffect(() => {
    loadExperiences();
  }, []);

  const loadExperiences = async () => {
    try {
      setIsLoading(true);
      const response = await experienceAPI.getAll();
      if (response.success) {
        setExperiences(response.data || []);
      }
    } catch (error) {
      console.error('Error loading experiences:', error);
      setMessage('Error loading experiences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExperience = async () => {
    try {
      setIsSaving(true);
      setMessage('');
      
      const newItem = {
        ...newExperience,
        id: newExperience.id || Date.now(),
        visible: newExperience.visible
      };
      const response = await experienceAPI.add(newItem);
      if (response.success) {
        setExperiences([response.data, ...experiences]);
        setNewExperience({ role: '', company: '', duration: '', summary: '', visible: true });
        setShowAddForm(false);
        setMessage('Experience added successfully!');
      }
    } catch (error) {
      console.error('Error adding experience:', error);
      setMessage('Error adding experience');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateExperience = async () => {
    try {
      setIsSaving(true);
      setMessage('');
      
      const newItem = {
        ...editingExperience,
        id: editingExperience.id,
        visible: editingExperience.visible
      };
      const response = await experienceAPI.update(editingExperience.id, newItem);
      if (response.success) {
        setExperiences(experiences.map(exp => exp.id === editingExperience.id ? response.data : exp));
        setEditingExperience(null);
        setMessage('Experience updated successfully!');
      }
    } catch (error) {
      console.error('Error updating experience:', error);
      setMessage('Error updating experience');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteExperience = async (id) => {
    if (!confirm('Are you sure you want to delete this experience?')) return;
    
    try {
      const response = await experienceAPI.delete(id);
      if (response.success) {
        setExperiences(experiences.filter(exp => exp.id !== id));
        setMessage('Experience deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting experience:', error);
      setMessage('Error deleting experience');
    }
  };

  // Add this function for toggling visibility in the experiences list and persist to backend
  const handleExperienceChange = async (index, field, value) => {
    const updatedItems = [...experiences];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };
    setExperiences(updatedItems);

    // Persist the change to the backend
    const updatedExperience = updatedItems[index];
    try {
      await experienceAPI.update(updatedExperience.id, updatedExperience);
    } catch (error) {
      console.error('Failed to update experience visibility:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Experience Manager</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <FaPlus />
          Add Experience
        </motion.button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Add Experience Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-700 rounded-lg"
          >
            <h4 className="text-lg font-medium text-white mb-4">Add New Experience</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Role/Position *
                </label>
                <input
                  type="text"
                  value={newExperience.role}
                  onChange={(e) => setNewExperience({ ...newExperience, role: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  value={newExperience.company}
                  onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="e.g., Google Inc."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Duration *
                </label>
                <input
                  type="text"
                  value={newExperience.duration}
                  onChange={(e) => setNewExperience({ ...newExperience, duration: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="e.g., Jan 2023 - Present"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Summary/Description *
                </label>
                <textarea
                  value={newExperience.summary}
                  onChange={(e) => setNewExperience({ ...newExperience, summary: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary resize-vertical"
                  placeholder="Describe your role, achievements, and responsibilities..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image (optional)</label>
                {newExperience.image_url && (
                  <div className="mb-2 flex items-center gap-4">
                    <img
                      src={newExperience.image_url}
                      alt="Experience"
                      className="w-12 h-12 object-cover rounded border-2 border-primary"
                      onError={e => { e.target.onerror = null; e.target.src = '/default-experience.png'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setNewExperience(prev => ({ ...prev, image_url: '' }))}
                      className="p-2 text-red-400 hover:text-red-300"
                    >
                      <FaTimes /> Remove
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async e => {
                    const file = e.target.files[0];
                    if (file) {
                      const response = await uploadAPI.uploadImage(file);
                      if (response.success) setNewExperience(prev => ({ ...prev, image_url: response.data.url }));
                    }
                  }}
                  className="block mt-2"
                />
              </div>

              <VisibilityToggle
                  checked={newExperience.visible}
                  onChange={e => setNewExperience({ ...newExperience, visible: e.target.checked })}
                loading={false}
                />
            </div>
            
            <div className="flex gap-3 mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddExperience}
                disabled={isSaving || !newExperience.role || !newExperience.company || !newExperience.duration || !newExperience.summary}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Adding...' : 'Add Experience'}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowAddForm(false);
                  setNewExperience({
                    role: '',
                    company: '',
                    duration: '',
                    summary: '',
                    visible: true
                  });
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <FaTimes />
                Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Experiences List */}
      <div className="space-y-4">
        {experiences.map((experience, index) => (
          <motion.div
            key={experience.id}
            layout
            className="bg-gray-700 rounded-lg p-4"
          >
            {editingExperience?.id === experience.id ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={editingExperience.role}
                    onChange={(e) => setEditingExperience({ ...editingExperience, role: e.target.value })}
                    className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                    placeholder="Role"
                  />
                  <input
                    type="text"
                    value={editingExperience.company}
                    onChange={(e) => setEditingExperience({ ...editingExperience, company: e.target.value })}
                    className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                    placeholder="Company"
                  />
                  <input
                    type="text"
                    value={editingExperience.duration}
                    onChange={(e) => setEditingExperience({ ...editingExperience, duration: e.target.value })}
                    className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary"
                    placeholder="Duration"
                  />
                  <textarea
                    value={editingExperience.summary}
                    onChange={(e) => setEditingExperience({ ...editingExperience, summary: e.target.value })}
                    rows={4}
                    className="md:col-span-2 px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary resize-vertical"
                    placeholder="Summary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Image (optional)</label>
                  {editingExperience?.image_url && (
                    <div className="mb-2 flex items-center gap-4">
                      <img
                        src={editingExperience.image_url}
                        alt="Experience"
                        className="w-12 h-12 object-cover rounded border-2 border-primary"
                        onError={e => { e.target.onerror = null; e.target.src = '/default-experience.png'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setEditingExperience(prev => ({ ...prev, image_url: '' }))}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <FaTimes /> Remove
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (file) {
                        const response = await uploadAPI.uploadImage(file);
                        if (response.success) setEditingExperience(prev => ({ ...prev, image_url: response.data.url }));
                      }
                    }}
                    className="block mt-2"
                  />
                </div>
                
                <VisibilityToggle
                    checked={editingExperience.visible}
                    onChange={e => setEditingExperience({ ...editingExperience, visible: e.target.checked })}
                  loading={false}
                  />

                <div className="flex gap-3">
                  <button
                    onClick={handleUpdateExperience}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingExperience(null)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    <FaTimes />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FaBriefcase className="text-primary" />
                    <h4 className="text-lg font-medium text-white">{experience.role}</h4>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    <FaBuilding className="text-gray-400" />
                    <span className="text-gray-300">{experience.company}</span>
                  </div>
                  
                  <div className="text-sm text-gray-400 mb-3">
                    {experience.duration}
                  </div>
                  
                  <p className="text-gray-300 whitespace-pre-wrap">
                    {experience.summary}
                  </p>
                </div>
                
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => setEditingExperience(experience)}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 rounded"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteExperience(experience.id)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ExperienceEditor; 